// Angular Files
import { KeyValue, Location } from '@angular/common';
import {
    NgZone,
    Input,
    Output,
    EventEmitter,
    OnInit,
    DoCheck,
    Directive,
    OnDestroy,
    inject
} from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';

// Angular Material Files
import { MatIconRegistry } from '@angular/material/icon';
import { MatLegacyDialog } from '@angular/material/legacy-dialog';

// Other External Files
import { isEqual, cloneDeep } from 'lodash';
import { firstValueFrom, Subscription } from 'rxjs';

// Payment Integration Files
import {
    PaymentMethodData,
    IFramePaymentResponse,
    IFrameSavePaymentMethodResponse,
    PaymentMethodTypeEnum,
    ECheckAccountTypeEnum,
    ECheckAccountOwnerTypeEnum
} from 'apps/public-portal/src/app/payment-integrations/base/models';
import { PaymentMethodTypeEnumConvertor, PaymentProcessorProvider } from 'apps/public-portal/src/app/payment-integrations';
import {
    ActiveAuthorizationTextDto,
    IErrorDto,
    PaymentAuthorizationTypeEnumDto,
    PaymentMethodTypeEnumDto
} from 'apps/public-portal/src/app/core/api/PublicPortalApiClients';
import { AuthorizationConsentDto } from 'apps/public-portal/src/app/core/api/PaymentIntegrationApiClients';
import {
    AuthorizationConsentComponent,
    AUTHORIZATION_REAUTH_NOTICE
} from 'apps/public-portal/src/app/shared/components/authorization-consent/authorization-consent.component';

// Teller Online Files
import {
    AuthService,
    AuthorizationConsentService,
    CartModel,
    CartService,
    InboundRedirectService
} from 'apps/public-portal/src/app/core/services';
import {
    PaymentConsentDialogComponent,
    PaymentConsentDialogData
} from 'apps/public-portal/src/app/shared/components/payment-consent-dialog/payment-consent-dialog.component';
import { DomSanitizer } from '@angular/platform-browser';
import { StateOrProvince, Country } from 'apps/public-portal/src/app/shared/constants';

// Teller Online Library Files
import { TellerOnlineSiteMetadataService, TellerOnlineAppService } from 'teller-online-libraries/core';
import {
    TellerOnlineMessageService,
    TellerOnlineValidationService
} from 'teller-online-libraries/shared';

@Directive()
export abstract class BasePaymentProcessorComponent implements OnInit, DoCheck, OnDestroy {
    // Declare @Input variables
    @Input() public allowSavePaymentMethod: boolean = false;

    // aria-labelledby is a fixed DOM attribute name and can't match camelCase.
    // eslint-disable-next-line @angular-eslint/no-input-rename
    @Input('aria-labelledby') public ariaLabelledBy: string;

    /** Whether or not this view is being used to edit a method; false if used to make a payment */
    @Input() public forEdit: boolean = false;

    @Input() public paymentMethodId: number = null;

    @Input() public set paymentMethodData(value) {
        this._paymentMethodData = value;

        const countryName = this._paymentMethodData.billingInfo.addressCountry?.name
            ?? this.siteMetadataService.appConfiguration.defaultCountry;
        this._paymentMethodData.billingInfo.addressCountry = Country.GetByName(countryName);

        if (Country.HasRegionList(this._paymentMethodData.billingInfo.addressCountry?.code)) {
            const stateName = this._paymentMethodData.billingInfo.addressState?.name
                ?? this.siteMetadataService.appConfiguration.defaultState;
            this._paymentMethodData.billingInfo.addressState = StateOrProvince.GetByName(stateName);
        }

        if (this.authService.isSignedIn) {
            if (!this._paymentMethodData.billingInfo.fullName) {
                this._paymentMethodData.billingInfo.fullName = this.authService.authDetails.name;
            }

            if (!this._paymentMethodData.billingInfo.email) {
                this._paymentMethodData.billingInfo.email = this.authService.authDetails.email;
            }
        } else if (this.inboundRedirectService.isInboundRedirect) {
            const fullName = this.inboundRedirectService.userFullName;
            if (!this._paymentMethodData.billingInfo.fullName && fullName) {
                this._paymentMethodData.billingInfo.fullName = fullName;
            }

            const email = this.inboundRedirectService.userEmail;
            if (!this._paymentMethodData.billingInfo.email && email) {
                this._paymentMethodData.billingInfo.email = email;
            }
        }

        this._setupDefaultObject(value);

        // Add the remember payment method checkbox, billing info will be
        // added in the billingInfoComponent
        this.paymentDetailsForm.addControl(
            'rememberPaymentMethod', new UntypedFormGroup({ checked: new UntypedFormControl(false) }));
        this._rememberPaymentMethodSubscription = this.paymentDetailsForm.controls
            .rememberPaymentMethod.valueChanges.subscribe(remember => {
            this._paymentMethodData.rememberPaymentMethod = remember.checked;
            // TOP-2935 follow-up: when the user opts to save the method during
            // ad-hoc checkout, prompt for SavedPaymentMethod consent in a modal
            // (the inline AdHoc clickwrap on the page only covers the one-off
            // debit, not the standing authorization). Skip for the save/edit
            // flow (forEdit) — that flow's inline consent already collects
            // SavedPaymentMethod consent.
            if (this.forEdit) return;
            if (!remember.checked) {
                this.savedPaymentMethodAuthorizationConsent = null;
                return;
            }
            if (this.savedPaymentMethodAuthorizationConsent) return;
            this._promptForSaveMethodDuringAdHocConsent();
        });
    }

    get paymentMethodData() {
        return this._paymentMethodData;
    }

    get testTriggerData() {
        if (!this._testTriggerData) {
            const testTriggerConfig = this.paymentProvider
                .getTestTriggerData(this.paymentMethodData?.type);
            if (testTriggerConfig) {
                this._testTriggerData = Object.keys(testTriggerConfig).map(key => ({
                    key,
                    value: testTriggerConfig[key],
                }));
            }
        }

        return this._testTriggerData;
    }

    protected _paymentMethodData: PaymentMethodData = new PaymentMethodData();

    protected _testTriggerData: { key: string, value: string }[];

    // Declare @Output variables
    @Output() public processingComplete = new EventEmitter<
        IFramePaymentResponse | IFrameSavePaymentMethodResponse>();

    @Output() public processingError = new EventEmitter<unknown>();

    @Output() public dataEntryStarted = new EventEmitter<boolean>();

    // Public variables
    public loading: boolean = false;

    public requireDualEntryForECheck: boolean = false;

    public PaymentMethodTypeEnum = PaymentMethodTypeEnum;

    public ECHECK_ACCOUNTTYPELIST: ECheckAccountTypeEnum[] = [
        ECheckAccountTypeEnum.Checking,
        ECheckAccountTypeEnum.Savings
    ];

    public ECHECK_ACCOUNTOWNERTYPELIST: ECheckAccountOwnerTypeEnum[] = [
        ECheckAccountOwnerTypeEnum.Personal,
        ECheckAccountOwnerTypeEnum.Business
    ];

    public paymentDetailsForm: UntypedFormGroup = new UntypedFormGroup({});

    // TOP-2935 consent state. Bound to the (consentChange) output of
    // <app-authorization-consent> in each integrator's template. The submit
    // path (payCart / savePaymentMethod / etc. in subclasses) embeds this
    // verbatim in the request payload. A non-null payload plus the user
    // clicking submit is the consent (inline clickwrap).
    public authorizationConsent: AuthorizationConsentDto | null = null;

    /** Reference to the consent component for stale-version inline re-consent
        (deviation #4). Set via `@ViewChild` in subclasses that handle the
        AuthorizationVersionStale error response and need to call
        `replaceVersion()`. */
    public authorizationConsentComponent: AuthorizationConsentComponent | null = null;

    /** Authorization-type for the consent component's `[authorizationType]`
        input. `forEdit === true` → SavedPaymentMethod (save flow);
        `forEdit === false` → AdHoc (pay-cart flow). Scheduled-payment use case
        is wired by `schedule.component.ts` directly (Phase F), not here. */
    public get authorizationType(): PaymentAuthorizationTypeEnumDto {
        return this.forEdit
            ? PaymentAuthorizationTypeEnumDto.SavedPaymentMethod
            : PaymentAuthorizationTypeEnumDto.AdHoc;
    }

    /** PaymentMethodTypeEnumDto for the consent component's `[paymentMethodType]`
        input (Swagger-generated DTO enum, not the frontend internal enum). */
    public get paymentMethodTypeDto(): PaymentMethodTypeEnumDto {
        return PaymentMethodTypeEnumConvertor.toDto(this._paymentMethodData?.type);
    }

    /**
     * TOP-2935 follow-up: SavedPaymentMethod consent captured when the user
     * toggles "remember payment method" on during ad-hoc checkout. Cleared
     * when they toggle it back off. Forwarded by each integrator's payCart()
     * payload on `savedPaymentMethodAuthorizationConsent`. Null on the
     * save/edit flow (forEdit) — that flow uses the inline SavedPaymentMethod
     * consent surface instead.
     */
    public savedPaymentMethodAuthorizationConsent: AuthorizationConsentDto | null = null;

    /**
     * TOP-2935 follow-up: submit-button label reflects whether clicking the
     * button is an authorization act for any consent in play — inline AdHoc
     * clickwrap on the page, or a SavedPaymentMethod consent captured
     * earlier via the in-checkout modal.
     *
     *  - "Save" on the forEdit (profile save / update) flow.
     *  - "Authorize and Pay" when ANY consent has resolved — clickwrap
     *    semantics on the page, OR a modal-captured standing
     *    authorization that legally covers this specific debit (the
     *    reauth-off save-during-ad-hoc case; the SavedPaymentMethod
     *    consent the user just authorized in the modal is the resolver's
     *    Rung 3 authorization for this Payment).
     *  - "Pay" only when no consent is in play at all — Credit Card on
     *    a tenant with no AdHoc text configured (the legitimate no-op
     *    from QA C7).
     */
    public get submitButtonLabel(): string {
        if (this.forEdit) return 'Save';
        if (this.authorizationConsent !== null
            || this.savedPaymentMethodAuthorizationConsent !== null) {
            return 'Authorize and Pay';
        }
        return 'Pay';
    }

    /**
     * TOP-2935 follow-up: gates the page-level inline AdHoc clickwrap. Visible
     * by default. When the user has authorized the SavedPaymentMethod consent
     * via the in-checkout modal AND the tenant does NOT require per-payment
     * AdHoc re-authorization for this PMT, the SavedPaymentMethod consent
     * already covers the initial debit so the inline AdHoc clickwrap is
     * redundant and is hidden. When reauth IS required, the inline clickwrap
     * stays visible to collect the AdHoc consent for this specific debit.
     */
    public get showInlineAdHocConsent(): boolean {
        if (this.forEdit) return true;

        // In Modal Mode the consent block is collected via a dialog before
        // payCart() fires; suppress the inline block so the payer doesn't see
        // a duplicate disclosure.
        if (this.siteMetadataService.presentConsentInModal) return false;

        if (!this._paymentMethodData?.rememberPaymentMethod) return true;
        if (!this.savedPaymentMethodAuthorizationConsent) return true;
        const requiresReauth = this._paymentMethodData.type === PaymentMethodTypeEnum.ECheck
            ? this.siteMetadataService.requireSavedMethodReauthorizationECheck
            : this.siteMetadataService.requireSavedMethodReauthorizationCreditCard;
        return requiresReauth;
    }

    // Constants
    // Field names and their respective values to be used in messages
    public CC_FIELDS = {
        ccname: "Card Holder's Name",
        ccnumber: 'Card Number',
        ccexp: 'Valid Till',
        cvv: 'CVV'
    };

    public ECHECK_FIELDS = {
        checkname: "Account Holder's Name",
        checktype: 'Account Type',
        checkowner: 'Account Owner Type',
        checkaba: 'Routing Number',
        checkabavalidator: 'Confirm Routing Number',
        checkaccount: 'Account Number',
        checkaccountvalidator: 'Confirm Account Number'
    };

    // Private variables, only used in this file
    private _dataEntryStarted: boolean = false;

    private _cartItemCount: number;

    // defaults used for checking if data has been modified
    private _defaultPaymentMethodData: {
        data: PaymentMethodData,
        starting: string,
        details: string,
        detailsCountry: string
    } = {
        data: new PaymentMethodData(),
        starting: null,
        details: null,
        detailsCountry: null
    };

    // Protected variables - private within any extended components
    protected _paymentToken: string;

    protected _cartId: number;

    protected _cartGuid: string;

    protected _cartVersion: number = 0;

    protected _processingPayment: boolean = false;

    protected _selectedTestTrigger: string = '';

    // Subscriptions
    private _cartSubscription: Subscription;

    private _rememberPaymentMethodSubscription: Subscription;

    // TOP-2935 follow-up: opened when the user toggles "remember payment method"
    // on during ad-hoc checkout to collect the SavedPaymentMethod consent. Using
    // inject() instead of a constructor parameter keeps the change scoped to
    // this file — the 11 integrator subclasses don't need to thread MatDialog
    // through their own super() calls.
    private _dialog = inject(MatLegacyDialog);

    // Used by _resolveAdHocConsentForModalMode() to pre-check whether active
    // text exists before opening the modal. Avoids showing a
    // permanently-disabled dialog in Credit no-op flows.
    private _authorizationConsentService = inject(AuthorizationConsentService);

    constructor(
        protected appService: TellerOnlineAppService,
        protected ngZone: NgZone,
        protected location: Location,
        protected siteMetadataService: TellerOnlineSiteMetadataService,
        public inboundRedirectService: InboundRedirectService,
        public cartService: CartService,
        public authService: AuthService,
        public messageService: TellerOnlineMessageService,
        public validationService: TellerOnlineValidationService,
        public paymentProvider: PaymentProcessorProvider,
        public matIconRegistry?: MatIconRegistry,
        public domSanitizer?: DomSanitizer,
    ) {
        if (matIconRegistry) {
            // url path must be relative to this file
            matIconRegistry.addSvgIcon('echeck-start', domSanitizer.bypassSecurityTrustResourceUrl('../../../../assets/icons/echeck-icon.svg'));
            matIconRegistry.addSvgIcon('echeck-end', domSanitizer.bypassSecurityTrustResourceUrl('../../../../assets/icons/echeck-icon-s.svg'));
        }
        this.requireDualEntryForECheck = paymentProvider.defaultConfig.requireDualEntryForECheck;
    }

    ngOnInit() {
        this._cartSubscription = this.cartService.cart$
            .subscribe(cart => this._handleCartSubscription(cart));
    }

    ngDoCheck(): void {
        // If something changed, we need to track that for use in a "are there
        // unsaved changes" detection. But we don't want to do it if we're
        // currently processing the payment because a redirect will happen
        // after and if we throw this error it will be a bad user experience
        if (!this._dataEntryStarted && !this._processingPayment) {
            // If our current data is different from our starting values
            // and doesn't match any of the default data, data entry has started
            if (!isEqual(this.paymentMethodData, this._defaultPaymentMethodData.starting)
                && !isEqual(this.paymentMethodData, this._defaultPaymentMethodData.details)) {
                this.startedDataEntry();
            }
        }
    }

    ngOnDestroy(): void {
        if (this._rememberPaymentMethodSubscription) {
            this._rememberPaymentMethodSubscription.unsubscribe();
        }
        if (this._cartSubscription) this._cartSubscription.unsubscribe();
    }

    /** For use in an ngFor on an object's fields in order to not sort the fields */
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    originalOrder = (a: KeyValue<string, string>, b: KeyValue<string, string>): number => 0;

    selectTestTrigger(selectedTestTrigger: string) {
        this._selectedTestTrigger = selectedTestTrigger;
    }

    public abstract payCart();

    public abstract savePaymentMethod();

    /**
     * Bound to `(consentChange)` of the consent component. Stores the latest
     * consent payload so the submit path can read it without going through
     * `@ViewChild`. With inline clickwrap the submit button is the consent act,
     * so nothing gates the button on this payload.
     */
    public onConsentChange(consent: AuthorizationConsentDto | null): void {
        this.authorizationConsent = consent;
    }

    /**
     * Bound to `(componentReady)` of the consent component. Captures the
     * component instance so the submit path can invoke `replaceVersion()` on
     * stale-version error responses without each integrator declaring its own
     * `@ViewChild`.
     */
    public onConsentReady(component: AuthorizationConsentComponent): void {
        this.authorizationConsentComponent = component;
    }

    /**
     * Called by subclasses after catching a `AuthorizationVersionStale` error
     * response. Re-renders the consent block onto the new active version
     * inline (deviation #4) so the user's other form state survives.
     */
    public handleStaleAuthorizationVersion(newActive: ActiveAuthorizationTextDto): void {
        this.authorizationConsentComponent?.replaceVersion(newActive);
    }

    /**
     * Convenience used by subclass catch blocks. Inspects the caught error for
     * the stale-version shape and, if matched and the consent component is
     * bound via `@ViewChild`, invokes the inline re-consent path and returns
     * `true`. Returns `false` for any other error so the subclass falls
     * through to its existing handling.
     *
     * Standard subclass pattern:
     *     } catch (e) {
     *         if (this.tryHandleStaleAuthorizationVersion(e)) return;
     *         // existing catch logic
     *     }
     */
    public tryHandleStaleAuthorizationVersion(error: IErrorDto): boolean {
        if (error?.errorDef !== 'AuthorizationVersionStale'
            || !error?.activeAuthorizationText) {
            return false;
        }

        // Inline Mode: re-render the consent block onto the new version in place.
        if (this.authorizationConsentComponent) {
            this.handleStaleAuthorizationVersion(
                error.activeAuthorizationText as ActiveAuthorizationTextDto);
            return true;
        }

        // Modal Mode: the inline block is suppressed, so prompt the payer to
        // re-authorize against the updated text on their next submit.
        if (this.siteMetadataService.presentConsentInModal) {
            this.messageService.notification(AUTHORIZATION_REAUTH_NOTICE, 'info', 5000);
            return true;
        }

        return false;
    }

    /**
     * A Hook that is called anytime an actionable cart (newest version, not
     * loading) is provided in the subscription.
     * PSPs with their own prepare step (e.g. FIS) should override this with custom logic.
     * @param cart The cart provided by the cartService.cart$ observable
     */
    /* eslint-disable-next-line @typescript-eslint/no-unused-vars,
       @typescript-eslint/no-empty-function */
    public async handleCartChanges(cart: CartModel, cartItemCount: number): Promise<void> {
    }

    // This method isn't necessary, but given how long the property is it makes it easier to read
    public getFieldError(fieldName) {
        return this.paymentDetailsForm.controls[fieldName]?.value?.error ?? '';
    }

    protected startedDataEntry() {
        this._dataEntryStarted = true;
        this.dataEntryStarted.emit(true);
    }

    protected finishedDataEntry(processingPayment = false) {
        if (processingPayment) this._processingPayment = true;
        this._dataEntryStarted = false;
        this.dataEntryStarted.emit(false);
    }

    protected updateUrl() {
        const url = `/profile/payment-methods/${this.paymentMethodId}`;
        this.appService.currentUrl = url;
        this.location.replaceState(url);
    }

    shouldDisableECheckSubmit() {
        // TOP-2935: consent is now inline clickwrap — clicking the submit button
        // IS the consent, so it is never disabled on a consent condition. Retained
        // (returning false) because integrator templates and msb.component.ts still
        // reference it in their [disabled] expressions.
        return false;
    }

    /** Return the appropriate error message for an integration field field
     * using a lookup and appending appropriate error
     */
    protected getIntegrationFieldErrorMessage(field: string, error: 'required' | 'invalid' = 'required') {
        const emptyMessage: string = ' is required.';
        const invalidMessage: string = ' is invalid.';

        let message = (this.CC_FIELDS[field] ?? this.ECHECK_FIELDS[field]);
        // Ensure we have some sort of name as a fall back, though we should never see this
        if (!message) message = 'Field';

        switch (error) {
            case 'required':
                message += emptyMessage;
                break;
            case 'invalid':
                message += invalidMessage;
                break;
            // ensure there is a fallback, though we should never see this
            default:
                message += ' has an error.';
        }
        return message;
    }

    // Setup the default variables used for checking equality when changes are made to form data
    private _setupDefaultObject(paymentMethodData?: PaymentMethodData) {
        if (!paymentMethodData) paymentMethodData = this.paymentMethodData;

        // Ensure that the default data is a copy of whatever paymentMethodData is right now
        this._defaultPaymentMethodData.data = cloneDeep(paymentMethodData);
        // "starting" is considered whatever the value is right now before modification
        this._defaultPaymentMethodData.starting = cloneDeep(this._defaultPaymentMethodData.data);

        // Add the default user details if the user is signed in (and we
        // don't have a fullName yet)
        if (this.authService.isSignedIn
            && !this._defaultPaymentMethodData.data.billingInfo.fullName) {
            const { name, email } = this.authService.authDetails;
            this._defaultPaymentMethodData.data.billingInfo.fullName = name;
            this._defaultPaymentMethodData.data.billingInfo.email = email;
        }
        this._defaultPaymentMethodData.details = cloneDeep(this._defaultPaymentMethodData.data);
    }

    /**
     * Get information from new versions of the cart, and execute a hook to
     * allow child classes to handle the new cart.
     * @param cart
     */
    /**
     * TOP-2935 follow-up: opens the in-checkout SavedPaymentMethod consent
     * modal when the user toggles "remember payment method" on. Stores the
     * resulting DTO for the integrator's payCart() to forward. On cancel,
     * reverts the toggle so the user can re-decide.
     */
    private async _promptForSaveMethodDuringAdHocConsent(): Promise<void> {
        // Pre-check: skip the modal if no active text is configured (Credit
        // no-op). Mirrors _resolveAdHocConsentForModalMode's identical guard —
        // without it, the dialog opens with its Authorize button permanently
        // disabled (canAuthorize gates on consent !== null, which can't tell
        // "no consent required" apart from "not yet resolved") and the only
        // way out is Cancel, which reverts the checkbox.
        const activeText = await firstValueFrom(
            this._authorizationConsentService.getActive(
                this.paymentMethodTypeDto, PaymentAuthorizationTypeEnumDto.SavedPaymentMethod));
        if (!activeText) return;

        // Dynamic import to break a static module cycle: base-payment-processor
        // is in payment-integrations/ and the dialog lives in features/checkout/.
        // A static import created a path payment-integrations -> features/checkout
        // -> shared/components -> ... -> payment-integrations, surfacing as a TDZ
        // "Cannot access 'AuthorizationConsentModule' before initialization"
        // in the Karma bundle. Lazy-loading the dialog module on first open keeps
        // the static dep graph layered.
        const { SaveMethodDuringAdHocConsentComponent } = await import(
            'apps/public-portal/src/app/features/checkout/dialogs/save-method-during-adhoc-consent/save-method-during-adhoc-consent.component'
        );
        const dialogRef = this._dialog.open(SaveMethodDuringAdHocConsentComponent, {
            data: { paymentMethodType: this.paymentMethodTypeDto, activeText },
            width: '600px',
            disableClose: true,
            autoFocus: false
        });
        const consent = await firstValueFrom(dialogRef.afterClosed());
        if (consent) {
            this.savedPaymentMethodAuthorizationConsent = consent;
            // TOP-2935 follow-up: AuthorizationConsentComponent auto-emits its
            // consent payload on init, which leaves `this.authorizationConsent`
            // populated with the AdHoc consent BEFORE the user has interacted
            // with anything. If the inline AdHoc clickwrap is now about to be
            // hidden (reauth-off for this PMT after a successful save-method
            // authorization), that auto-emitted AdHoc consent is stale — the
            // user only actively agreed to the SavedPaymentMethod consent in
            // the modal. Clear it so the backend records only the consent the
            // user actually clicked through, and the ConsentConfirmation email
            // surfaces the SavedPaymentMethod text rather than the AdHoc text.
            if (!this.showInlineAdHocConsent) {
                this.authorizationConsent = null;
            }
        } else {
            // User cancelled - revert the toggle. setValue fires valueChanges
            // which clears the stored consent via the subscription handler.
            this.paymentDetailsForm.controls.rememberPaymentMethod.setValue({ checked: false });
        }
    }

    /**
     * Modal Mode gate for ad-hoc payCart.
     *
     * When `presentConsentInModal` is true the inline clickwrap is hidden
     * (`showInlineAdHocConsent` returns false), so each integrator's payCart()
     * must call this helper BEFORE building its payload. Returns:
     *   true  — proceed with payCart (consent populated or not required).
     *   false — user cancelled the modal; payCart should return early.
     *
     * In Inline Mode (or forEdit) this is a no-op that returns true immediately.
     * For Credit no-op flows (no active text configured), also returns true
     * without opening the dialog so the payment proceeds with a null consent as
     * the backend expects (C7 / Credit no-text path).
     */
    protected async _resolveAdHocConsentForModalMode(): Promise<boolean> {
        if (this.forEdit || !this.siteMetadataService.presentConsentInModal) return true;

        // Pre-check: skip the modal if no active text is configured (Credit no-op).
        const activeText = await firstValueFrom(
            this._authorizationConsentService.getActive(
                this.paymentMethodTypeDto, PaymentAuthorizationTypeEnumDto.AdHoc));
        if (!activeText) return true;

        const dialogData: PaymentConsentDialogData = {
            paymentMethodType: this.paymentMethodTypeDto,
            authorizationType: PaymentAuthorizationTypeEnumDto.AdHoc,
            activeText
        };
        const dialogRef = this._dialog.open(PaymentConsentDialogComponent, {
            data: dialogData,
            width: '600px',
            disableClose: true,
            autoFocus: false
        });
        const consent = await firstValueFrom(dialogRef.afterClosed());
        if (!consent) return false;

        this.authorizationConsent = consent;
        return true;
    }

    private _handleCartSubscription(cart: CartModel) {
        // If this is an older version of the cart, we don't want it
        if (cart?.version && cart.version < this._cartVersion) return;

        // Update the cart version
        this._cartVersion = cart?.version;

        // Ensure the cart has finished loading before doing anything
        if (!this.cartService.loadingCart) {
            this._cartId = cart.cartId;
            this._cartGuid = cart.cartGuid;

            if (cart && cart.cartId) {
                this.handleCartChanges(cart, this._cartItemCount);

                // Total count of items in the cart
                this._cartItemCount = cart.items?.length;
            } else {
                this._cartItemCount = 0;
            }
        }
    }
}
